/**
 * Weapon level, weapon XP, and the ladder each weapon unlocks its parts on.
 *
 * ## Two ladders, deliberately unconnected
 *
 * The player has a career level (1..55) in `src/game/Progression.js`, earned from
 * kills and persisted across sessions. It is not this. **Every weapon has its own
 * level, 1..40, with its own XP, earned only while that weapon is in your hands.**
 * Nothing here reads or writes career XP and nothing here should ever be "unified"
 * with it: the career ladder is what the *player* has done, the weapon ladder is
 * what the player has done *with this weapon*, and the second is the only one that
 * has any business deciding whether they may fit a compensator to it.
 *
 * ## The ladder is derived, not typed
 *
 * Twenty-three hand-written unlock tables would be nine hundred numbers that nobody
 * could keep consistent, and the first time an attachment was added or a
 * compatibility rule changed, some of them would quietly be wrong. So each weapon's
 * ladder is *computed* from the parts that actually fit it, spread evenly across
 * levels 2 to 39 in a fixed order (`unlockOrder`, then slot, then id — all stable),
 * with its mastery at 40. Adding a part to the catalog reflows every ladder that can
 * take it, correctly, with no edit here.
 *
 * ## Optics are shared
 *
 * An optic earned on one weapon appears on every weapon that shares its mount group
 * (`compatibility.js`), with no further grinding. A sight is a sight; making a
 * player earn the same red dot six times is not progression, it is a tax on
 * variety. Everything else is per weapon.
 */

import { WEAPONS } from '../weapons.data.js';
import { CATALOG } from './catalog.js';
import { MASTERY, MASTERY_BY_WEAPON } from './mastery.js';
import { fits, opticGroupFor } from './compatibility.js';
import { SLOT_INDEX } from './schema.js';

export const TUNING = {
  maxWeaponLevel: 40,

  /* --- the weapon XP curve. cost(L) = round25(base + scale * L^power) --- */
  curveBase: 90,
  curveScale: 14,
  curvePower: 1.30,
  curveQuantum: 25,

  /* --- XP sources. A weapon only earns while it is the one being fired. --- */
  xpKill: 120,
  xpHeadshot: 60, // on top of the kill
  xpHit: 8, // per damaging hit, so a losing fight is not worth nothing
  xpMultiStep: 40,
  xpMultiMax: 200,
  multiWindow: 4.0,

  /** Levels the ordinary catalog is spread across. Mastery lands on the cap. */
  firstUnlockLevel: 2,
  lastUnlockLevel: 39,
};

/* -------------------------------------------------------------------------- */
/* the curve                                                                   */
/* -------------------------------------------------------------------------- */

const COST = buildCurve();
const CUMULATIVE = buildCumulative(COST);

function buildCurve() {
  const out = new Array(TUNING.maxWeaponLevel + 1).fill(0);
  const { curveBase: b, curveScale: s, curvePower: p, curveQuantum: q } = TUNING;
  let prev = 0;
  for (let L = 1; L < TUNING.maxWeaponLevel; L++) {
    // Strictly increasing by construction, exactly as the career curve is: if
    // quantisation ties two adjacent levels the later one is bumped, so "each level
    // costs more than the last" is a guarantee and not a property of the constants.
    let c = Math.round((b + s * Math.pow(L, p)) / q) * q;
    if (c <= prev) c = prev + q;
    out[L] = c;
    prev = c;
  }
  return out;
}

function buildCumulative(cost) {
  const out = new Array(TUNING.maxWeaponLevel + 2).fill(0);
  for (let L = 1; L < TUNING.maxWeaponLevel; L++) out[L + 1] = out[L] + cost[L];
  out[TUNING.maxWeaponLevel + 1] = out[TUNING.maxWeaponLevel];
  return out;
}

function clampLevel(L) {
  const n = Math.floor(Number(L));
  if (!Number.isFinite(n)) return 1;
  return Math.min(TUNING.maxWeaponLevel, Math.max(1, n));
}

/** XP to go from `level` to `level + 1`. 0 at the cap. */
export function weaponXpForLevel(level) {
  return COST[clampLevel(level)] ?? 0;
}

/** Total weapon XP required to *be* `level`. */
export function weaponXpToReach(level) {
  return CUMULATIVE[clampLevel(level)] ?? 0;
}

/** The level a weapon's lifetime XP corresponds to. */
export function weaponLevelForXp(xp) {
  const x = Number.isFinite(xp) ? Math.max(0, xp) : 0;
  let L = 1;
  while (L < TUNING.maxWeaponLevel && x >= CUMULATIVE[L + 1]) L++;
  return L;
}

/** The whole weapon ladder as rows, for the gunsmith screen and for reporting. */
export function weaponCurveTable() {
  const rows = [];
  for (let L = 1; L <= TUNING.maxWeaponLevel; L++) {
    rows.push({ level: L, cost: COST[L], cumulative: CUMULATIVE[L] });
  }
  return rows;
}

/* -------------------------------------------------------------------------- */
/* the per-weapon ladder                                                       */
/* -------------------------------------------------------------------------- */

/** Every attachment id that fits this weapon, mastery last. */
export function compatibleFor(weaponId) {
  const out = [];
  for (const a of Object.values(CATALOG)) if (fits(a, weaponId)) out.push(a.id);
  const m = MASTERY_BY_WEAPON[weaponId];
  if (m && fits(MASTERY[m], weaponId)) out.push(m);
  return out;
}

/**
 * Attachment id -> the weapon level it unlocks at.
 *
 * The ordinary catalog is spread across `firstUnlockLevel .. lastUnlockLevel` in a
 * stable order, so early levels hand out the basics (a vertical grip, a
 * compensator, a micro dot) and the late ones hand out the parts that change what
 * the weapon is (thermal, belt box, a fire-mode conversion). Mastery is on the cap.
 *
 * More parts than levels is normal and fine — a weapon with fifty-odd compatible
 * parts hands out one or two per level rather than one every level and a half.
 */
export function unlockLadder(weaponId) {
  const ids = Object.values(CATALOG)
    .filter((a) => fits(a, weaponId))
    .sort(byLadderOrder)
    .map((a) => a.id);

  const first = TUNING.firstUnlockLevel;
  const last = TUNING.lastUnlockLevel;
  const span = last - first + 1;
  const out = new Map();
  const n = ids.length;
  for (let i = 0; i < n; i++) {
    const level = n <= 1 ? first : Math.min(last, first + Math.floor((i * span) / n));
    out.set(ids[i], level);
  }
  const m = MASTERY_BY_WEAPON[weaponId];
  if (m) out.set(m, TUNING.maxWeaponLevel);
  return out;
}

function byLadderOrder(a, b) {
  if (a.unlockOrder !== b.unlockOrder) return a.unlockOrder - b.unlockOrder;
  const sa = SLOT_INDEX[a.slot] ?? 0;
  const sb = SLOT_INDEX[b.slot] ?? 0;
  if (sa !== sb) return sa - sb;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** Every ladder, computed once at module load. Weapon id -> Map(attId -> level). */
export const LADDERS = {};
for (const id of Object.keys(WEAPONS)) LADDERS[id] = unlockLadder(id);

/** The level `attId` unlocks at on `weaponId`, or null if it does not fit. */
export function unlockLevelFor(weaponId, attId) {
  return LADDERS[weaponId]?.get(attId) ?? null;
}

/** What a given weapon level hands out. For the level-up banner. */
export function unlocksAtWeaponLevel(weaponId, level) {
  const out = [];
  const ladder = LADDERS[weaponId];
  if (!ladder) return out;
  for (const [attId, lv] of ladder) if (lv === level) out.push(attId);
  return out;
}

/* -------------------------------------------------------------------------- */
/* shared optic unlocks                                                        */
/* -------------------------------------------------------------------------- */

/** The key an optic unlock is recorded under. Shared across a mount group. */
export function opticUnlockKey(weaponId, attId) {
  return `${opticGroupFor(weaponId)}:${attId}`;
}

/**
 * Every optic-unlock key a weapon level earns, for recording into the shared set.
 *
 * Called on every level-up and once when a save is loaded, so a player who levelled
 * a rifle to 30 before this system existed still finds the optics on their other
 * rifles rather than having to re-earn them.
 */
export function opticKeysEarned(weaponId, level) {
  const out = [];
  const ladder = LADDERS[weaponId];
  if (!ladder) return out;
  for (const [attId, lv] of ladder) {
    if (lv > level) continue;
    const a = CATALOG[attId];
    if (a?.slot === 'optic') out.push(opticUnlockKey(weaponId, attId));
  }
  return out;
}
